import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import des composants
import ErrorBoundary  from './components/ErrorBoundary';
import Login          from './components/Login';
import Dashboard      from './components/Dashboard';
import SoustraitantList   from './components/SoustraitantList';
import SoustraitantDetail from './components/SoustraitantDetail';
import DC4List        from './components/DC4List';
import DC4Form        from './components/DC4Form';
import MarcheList     from './components/MarcheList';
import MarcheDetail   from './components/MarcheDetail';
import Calendrier     from './components/Calendrier';
import ClientList        from './components/ClientList';
import ClientDetail      from './components/ClientDetail';
import Administration from './components/Administration';

// ─── Intercepteur axios : deconnexion auto sur token expire ──────────────────
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Token expire ou invalide : deconnecter
      if (localStorage.getItem('token')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── Garde de route ───────────────────────────────────────────────────────────
function PrivateRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'Admin') return (
    <div className="main-content">
      <div className="card">
        <div style={{ textAlign: 'center', padding: '3rem', color: '#721c24' }}>
          <h2>⛔ Accès refusé</h2>
          <p>Cette section est réservée aux administrateurs.</p>
        </div>
      </div>
    </div>
  );
  return children;
}

// ─── Composant principal ──────────────────────────────────────────────────────
function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Restaurer la session depuis localStorage au démarrage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setAuthChecked(true);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Attendre la vérification de session avant de rendre quoi que ce soit
  if (!authChecked) return null;

  return (
    <ErrorBoundary>
    <Router>
      <div className="App">

        {/* Navbar — affichée seulement si connecté */}
        {user && (
          <nav className="navbar">
            <div className="nav-container">
              <h1 className="nav-title">Gestion des Marchés</h1>
              <ul className="nav-menu">
                <li><Link to="/">Tableau de bord</Link></li>
                <li><Link to="/clients">Clients</Link></li>
                <li><Link to="/soustraitants">Sous-traitants</Link></li>
                <li><Link to="/marches">Marchés</Link></li>
                <li><Link to="/calendrier">Calendrier</Link></li>
                <li><Link to="/dc4">DC4</Link></li>
                {user.role === 'Admin' && (
                  <li><Link to="/administration">Administration</Link></li>
                )}
              </ul>

              {/* Info utilisateur + déconnexion */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ color: '#ecf0f1', fontSize: '0.875rem', textAlign: 'right' }}>
                  <div style={{ fontWeight: 600 }}>{user.prenom} {user.nom}</div>
                  <div style={{ opacity: 0.7, fontSize: '0.75rem' }}>
                    {user.role}{user.entreprise_nom ? ` · ${user.entreprise_nom}` : ''}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                    color: '#fff', padding: '0.4rem 0.9rem', borderRadius: '4px',
                    cursor: 'pointer', fontSize: '0.875rem', whiteSpace: 'nowrap',
                  }}
                >
                  Déconnexion
                </button>
              </div>
            </div>
          </nav>
        )}

        <main className={user ? 'main-content' : ''}>
          <Routes>
            {/* Page de login */}
            <Route path="/login" element={
              user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
            } />

            {/* Routes protégées */}
            <Route path="/" element={
              <PrivateRoute user={user}><Dashboard /></PrivateRoute>
            } />
            <Route path="/clients" element={
              <PrivateRoute user={user}><ClientList /></PrivateRoute>
            } />
            <Route path="/clients/:codification" element={
              <PrivateRoute user={user}><ClientDetail /></PrivateRoute>
            } />
            <Route path="/soustraitants" element={
              <PrivateRoute user={user}><SoustraitantList /></PrivateRoute>
            } />
            <Route path="/soustraitants/:codification" element={
              <PrivateRoute user={user}><SoustraitantDetail /></PrivateRoute>
            } />
            <Route path="/marches" element={
              <PrivateRoute user={user}><MarcheList user={user} /></PrivateRoute>
            } />
            <Route path="/marches/:id" element={
              <PrivateRoute user={user}><MarcheDetail user={user} /></PrivateRoute>
            } />
            <Route path="/calendrier" element={
              <PrivateRoute user={user}><Calendrier user={user} /></PrivateRoute>
            } />
            <Route path="/dc4" element={
              <PrivateRoute user={user}><DC4List user={user} /></PrivateRoute>
            } />
            <Route path="/dc4/new" element={
              <PrivateRoute user={user}><DC4Form user={user} /></PrivateRoute>
            } />
            <Route path="/dc4/:id" element={
              <PrivateRoute user={user}><DC4Form user={user} /></PrivateRoute>
            } />

            {/* Route Admin uniquement */}
            <Route path="/administration" element={
              <AdminRoute user={user}>
                <Administration currentUser={user} />
              </AdminRoute>
            } />

            {/* Redirection par défaut */}
            <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
          </Routes>
        </main>
      </div>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
